import { faBan, faGhost, faTrash } from '@fortawesome/free-solid-svg-icons';
import FormInput from '@form/FormInput';
import FormSubmit from '@form/FormSubmit';
import { Blockquote } from '@ui';
import { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
    name?: string;
    // undefined when no schedule check runs next to the form, then the cards are always offered
    conflictCount?: number;
}

// Lets the admin overbook a slot: what happens to the allocations the new one overlaps
const ConflictResolutionCards = ({ name = 'conflict_resolution', conflictCount }: Props) => {
    const { t } = useTranslation('space-manager');
    const { setValue } = useFormContext();

    // a choice made for conflicts that are gone must not silently apply to ones that show up later
    useEffect(() => {
        if (conflictCount === 0) {
            setValue(name, '');
        }
    }, [conflictCount, name, setValue]);

    if (conflictCount === 0) {
        return null;
    }

    return (
        <div className="tw-mb-4">
            <div className="tw-mb-2 tw-font-semibold">{t('allocation.form.conflictResolution.label')}</div>
            <Blockquote color="warning" className="tw-mb-4">
                {conflictCount === undefined
                    ? t('allocation.form.conflictResolution.introUnknown')
                    : t('allocation.form.conflictResolution.intro', { count: conflictCount })}
            </Blockquote>
            <FormInput.OptionCards
                name={name}
                allowDeselect
                ariaLabel={t('allocation.form.conflictResolution.label')}
                options={[
                    {
                        value: 'shadow',
                        icon: faGhost,
                        label: t('allocation.form.conflictResolution.options.shadow.label'),
                        description: t('allocation.form.conflictResolution.options.shadow.description')
                    },
                    {
                        value: 'cancel',
                        icon: faBan,
                        label: t('allocation.form.conflictResolution.options.cancel.label'),
                        description: t('allocation.form.conflictResolution.options.cancel.description')
                    },
                    {
                        value: 'delete',
                        icon: faTrash,
                        label: t('allocation.form.conflictResolution.options.delete.label'),
                        description: t('allocation.form.conflictResolution.options.delete.description')
                    }
                ]}
            />
        </div>
    );
};

export default ConflictResolutionCards;

// Submit that stays disabled while overlaps exist and no option was picked
export const ConflictAwareSubmit = ({
    name = 'conflict_resolution',
    conflictCount,
    isLoading
}: Props & { isLoading: boolean }) => {
    const { t } = useTranslation('space-manager');
    const { control } = useFormContext();
    const {
        field: { value }
    } = useController({ name, control });

    const hasConflicts = !!conflictCount;
    const blocked = hasConflicts && !value;

    return (
        <FormSubmit isLoading={isLoading} disabled={blocked}>
            {blocked
                ? t('allocation.form.conflictResolution.submitBlocked')
                : hasConflicts
                  ? t('allocation.form.conflictResolution.submitOverbook')
                  : undefined}
        </FormSubmit>
    );
};
